<script setup>
import { ref, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';

import Footer from '@/Components/Footer.vue';
import TopNavBar from '@/Components/TopNavBar.vue';
import CompareBar from '@/Components/Compare/CompareBar.vue';

defineProps({
    title: String,
    menuItems: Array,
});

// SEO Handling
const page = usePage();

const updateHead = (seo) => {
    if (!seo || typeof document === 'undefined') return;

    if (seo.title) {
        document.title = seo.title;
    }

    // Remove all previously injected SEO tags
    document.querySelectorAll('[data-seo]').forEach(el => el.remove());

    if (seo.canonical) {
        const link = document.createElement('link');
        link.rel = 'canonical';
        link.href = seo.canonical;
        link.setAttribute('data-seo', 'true');
        document.head.appendChild(link);
    }

    if (seo.description) {
        const metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        metaDesc.content = seo.description;
        metaDesc.setAttribute('data-seo', 'true');
        document.head.appendChild(metaDesc);
    }

    if (seo.keywords) {
        const metaKeywords = document.createElement('meta');
        metaKeywords.name = 'keywords';
        metaKeywords.content = seo.keywords;
        metaKeywords.setAttribute('data-seo', 'true');
        document.head.appendChild(metaKeywords);
    }

    if (seo.openGraph) {
        Object.entries(seo.openGraph).forEach(([key, value]) => {
            const og = document.createElement('meta');
            og.setAttribute('property', `og:${key}`);
            og.content = value;
            og.setAttribute('data-seo', 'true');
            document.head.appendChild(og);
        });
    }

    if (seo.structuredData && Array.isArray(seo.structuredData)) {
        seo.structuredData.forEach(schema => {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.textContent = JSON.stringify(schema);
            script.setAttribute('data-seo', 'true');
            document.head.appendChild(script);
        });
    }
};

watch(
  () => page?.props?.seo,
  (seo) => {
    if (seo) updateHead(seo);
  },
  { immediate: true }
);
</script>

<template>
    <div class="site-wrapper">
        <TopNavBar />

        <div class="site-content">
            <header v-if="$slots.header">
                <div class="container-site py-6">
                    <slot name="header" />
                </div>
            </header>

            <main>
                <slot />
            </main>
        </div>

        <Footer />

        <CompareBar />
    </div>
</template>
